/**
 * Date / time formatting helpers used across the app.
 *
 * Five single-purpose functions, deliberately kept small:
 *   • parseBackendDate     — robust parser (the only piece that knows about
 *                            backend timezone quirks)
 *   • formatRelativeTime   — "5 minutes ago" / "in 2 hours" (relative copy)
 *   • formatAbsolute       — "Apr 25, 2026, 10:26 PM" (human-readable
 *                            absolute datetime; ideal for tooltips)
 *   • formatDate           — "04/25/2026" (locale-aware calendar date)
 *   • formatExpiryRelative — "Expires in 2 hours" / "Expired 5 minutes ago"
 *
 * Pick the one that matches the *intent* of the UI; do not stack them.
 */

/**
 * Parse a date input that may come from the backend without an explicit
 * timezone (FastAPI often returns naive ISO strings like
 * `2026-04-25T15:00:00`). Treat those as UTC so downstream math is stable
 * regardless of the viewer's local offset. Strings that already carry a
 * `Z` or `+hh:mm` are respected; numbers / `Date` are passed through.
 * Returns `null` when the input cannot be parsed.
 */
export function parseBackendDate(input: string | number | Date): Date | null {
  if (input == null) return null;
  let date: Date;
  if (typeof input === 'string') {
    const hasTimezoneInfo =
      input.endsWith('Z') ||
      input.includes('+') ||
      (input.includes('T') && input.split('T')[1]?.includes('-'));
    if (hasTimezoneInfo) {
      date = new Date(input);
    } else if (input.includes('T')) {
      date = new Date(input + 'Z');
    } else {
      date = new Date(input);
    }
    if (Number.isNaN(date.getTime())) {
      // Last-resort: re-parse without our UTC suffix.
      date = new Date(input);
    }
  } else {
    date = new Date(input);
  }
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Relative time copy, e.g. "just now", "5 minutes ago", "in 2 hours".
 * Returns an empty string for unparseable input so callers can fall back
 * cleanly without a try/catch.
 */
export function formatRelativeTime(
  input: string | number | Date,
  nowInput?: string | number | Date,
): string {
  const parsed = parseBackendDate(input);
  if (!parsed) return '';
  const now = nowInput ? parseBackendDate(nowInput)?.getTime() ?? Date.now() : Date.now();
  const time = parsed.getTime();

  const diffMs = time - now; // past => negative
  const abs = Math.abs(diffMs);

  const sec = 1000;
  const min = 60 * sec;
  const hr = 60 * min;
  const day = 24 * hr;
  const mon = 30 * day;
  const yr = 365 * day;

  if (abs < 45 * sec) return 'just now';
  if (abs < 90 * sec) return diffMs < 0 ? '1 minute ago' : 'in 1 minute';
  if (abs < 45 * min) {
    const v = Math.round(abs / min);
    return diffMs < 0 ? `${v} minutes ago` : `in ${v} minutes`;
  }
  if (abs < 90 * min) return diffMs < 0 ? '1 hour ago' : 'in 1 hour';
  if (abs < 22 * hr) {
    const v = Math.round(abs / hr);
    return diffMs < 0 ? `${v} hours ago` : `in ${v} hours`;
  }
  if (abs < 36 * hr) return diffMs < 0 ? '1 day ago' : 'in 1 day';
  if (abs < 25 * day) {
    const v = Math.round(abs / day);
    return diffMs < 0 ? `${v} days ago` : `in ${v} days`;
  }
  if (abs < 45 * day) return diffMs < 0 ? '1 month ago' : 'in 1 month';
  if (abs < 345 * day) {
    const v = Math.round(abs / mon);
    return diffMs < 0 ? `${v} months ago` : `in ${v} months`;
  }
  if (abs < 545 * day) return diffMs < 0 ? '1 year ago' : 'in 1 year';
  const v = Math.round(abs / yr);
  return diffMs < 0 ? `${v} years ago` : `in ${v} years`;
}

/**
 * Human-readable absolute datetime in the viewer's local timezone — e.g.
 * "Apr 25, 2026, 10:26 PM". Use this for tooltips that should add precise
 * timestamp information on top of a relative label, not for primary copy.
 */
export function formatAbsolute(
  input: string | number | Date,
  options?: { withSeconds?: boolean; locale?: string },
): string {
  const date = parseBackendDate(input);
  if (!date) return '';
  return new Intl.DateTimeFormat(options?.locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: options?.withSeconds ? '2-digit' : undefined,
  }).format(date);
}

/**
 * Locale-aware calendar date — "04/25/2026" (US) or "25/04/2026" (other
 * locales). Use when only the day matters and the time would be noise.
 */
export function formatDate(input: string | number | Date, locale: string = 'en-US'): string {
  const date = parseBackendDate(input);
  if (!date) return '';
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/**
 * Caption helper for "expiry" copy that reads naturally regardless of
 * whether the timestamp is in the past or the future. Examples:
 *   future → "Expires in 2 hours"
 *   past   → "Expired 5 minutes ago"
 *   bad    → "" (caller can fall back)
 */
export function formatExpiryRelative(
  input: string | number | Date | null | undefined,
): string {
  if (input == null) return '';
  const parsed = parseBackendDate(input);
  if (!parsed) return '';
  const verb = parsed.getTime() < Date.now() ? 'Expired' : 'Expires';
  return `${verb} ${formatRelativeTime(parsed)}`;
}
